export default class DivulgacaoService {
    constructor(http, store) {
        this._http = http;
        this._store = store;
    }

    /**
     * Lista todas as divulgações com paginação e filtros
     * @param {Object} filtros - Filtros a serem aplicados
     * @param {number} page - Página atual
     * @param {number} itemsPerPage - Itens por página
     * @param {string} sortBy - Campo para ordenação
     * @param {boolean} sortDesc - Direção da ordenação
     * @returns {Promise} - Promise com a resposta da API
     */
    listarDivulgacoes(
        filtros = {},
        page = 1,
        itemsPerPage = 10,
        sortBy = '',
        sortDesc = true
    ) {
        const params = {
            page: page,
            per_page: itemsPerPage,
            ...filtros
        };
        if (sortBy) {
            params.sort_by = sortBy;
            params.sort_desc = sortDesc ? 1 : 0;
        }

        return this._http.get('divulgacoes', { params });
    }

    /**
     * Cria uma nova divulgação
     * @param {Object} dadosDivulgacao - Dados da divulgação a ser criada
     * @returns {Promise} - Promise com a resposta da API
     */
    criarDivulgacao(dadosDivulgacao) {
        const formData = new FormData();

        formData.append('assunto', dadosDivulgacao.assunto);
        formData.append('status', dadosDivulgacao.status || 'rascunho');

        if (dadosDivulgacao.expiraEm) {
            formData.append('expira_em', dadosDivulgacao.expiraEm);
        }

        if (dadosDivulgacao.enviarVia) {
            formData.append(
                'envio_email',
                dadosDivulgacao.enviarVia.email ? 1 : 0
            );
            formData.append(
                'envio_popup',
                dadosDivulgacao.enviarVia.popup ? 1 : 0
            );
        }

        if (
            dadosDivulgacao.destinatariosAdicionais &&
            dadosDivulgacao.destinatariosAdicionais.length > 0
        ) {
            formData.append(
                'destinatarios_adicionais',
                JSON.stringify(dadosDivulgacao.destinatariosAdicionais)
            );
        }
        if (dadosDivulgacao.arquivo) {
            formData.append('imagem', dadosDivulgacao.arquivo);
        }

        return this._http.post('divulgacoes', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    /**
     * Atualiza uma divulgação existente
     * @param {number} id - ID da divulgação
     * @param {Object} dadosDivulgacao - Dados da divulgação a ser atualizada
     * @returns {Promise} - Promise com a resposta da API
     */
    atualizarDivulgacao(id, dadosDivulgacao) {
        const formData = new FormData();

        formData.append('assunto', dadosDivulgacao.assunto);
        formData.append('status', dadosDivulgacao.status || 'rascunho');
        formData.append('_method', 'PUT');

        if (dadosDivulgacao.expiraEm) {
            formData.append('expira_em', dadosDivulgacao.expiraEm);
        }

        if (dadosDivulgacao.enviarVia) {
            formData.append(
                'envio_email',
                dadosDivulgacao.enviarVia.email ? 1 : 0
            );
            formData.append(
                'envio_popup',
                dadosDivulgacao.enviarVia.popup ? 1 : 0
            );
        }

        if (
            dadosDivulgacao.destinatariosAdicionais &&
            dadosDivulgacao.destinatariosAdicionais.length > 0
        ) {
            formData.append(
                'destinatarios_adicionais',
                JSON.stringify(dadosDivulgacao.destinatariosAdicionais)
            );
        }

        if (dadosDivulgacao.arquivo) {
            formData.append('imagem', dadosDivulgacao.arquivo);
        }

        return this._http.put(`divulgacoes/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    /**
     * Salva uma divulgação (cria ou atualiza dependendo se tem ID)
     * @param {Object} dadosDivulgacao - Dados da divulgação
     * @returns {Promise} - Promise com a resposta da API
     */
    salvarDivulgacao(dadosDivulgacao) {
        if (dadosDivulgacao.id) {
            return this.atualizarDivulgacao(
                dadosDivulgacao.id,
                dadosDivulgacao
            );
        } else {
            return this.criarDivulgacao(dadosDivulgacao);
        }
    }

    /**
     * Salva edições de um rascunho (mantém como rascunho)
     * @param {number} id - ID da divulgação
     * @param {Object} dadosDivulgacao - Dados da divulgação a ser atualizada
     * @returns {Promise} - Promise com a resposta da API
     */
    salvarEdicaoRascunho(id, dadosDivulgacao) {
        const dadosComStatus = {
            ...dadosDivulgacao,
            status: 'rascunho'
        };
        return this.atualizarDivulgacao(id, dadosComStatus);
    }

    /**
     * Envia um rascunho diretamente (atualiza dados e status para 'enviado')
     * @param {number} id - ID da divulgação
     * @param {Object} dadosDivulgacao - Dados da divulgação a ser atualizada
     * @returns {Promise} - Promise com a resposta da API
     */
    async enviarRascunhoAgora(id, dadosDivulgacao) {
        // Primeiro atualiza os dados do rascunho
        const dadosComStatus = {
            ...dadosDivulgacao,
            status: 'enviado'
        };

        await this.atualizarDivulgacao(id, dadosComStatus);
    }

    /**
     * Busca uma divulgação específica por ID
     * @param {number} id - ID da divulgação
     * @returns {Promise} - Promise com a resposta da API
     */
    buscarDivulgacao(id) {
        return this._http.get(`divulgacoes/${id}`);
    }

    /**
     * Busca atualizações (divulgações)
     * @returns {Promise} - Promise com a resposta da API
     */
    buscarAtualizacoes() {
        return this._http.get('divulgacoes/atualizacoes');
    }

    /**
     * Marca uma divulgação como visualizada
     * @param {number} id - ID da divulgação
     * @returns {Promise} - Promise com a resposta da API
     */
    marcarComoVisualizado(id) {
        return this._http.post('divulgacoes/atualizacoes', {
            divulgacao_id: id,
            cd_apresentante: this._store.getters.cdApresentante
        });
    }

    /**
     * Valida se uma imagem é válida para upload
     * @param {File} arquivo - Arquivo a ser validado
     * @returns {Object} - Resultado da validação
     */
    validarImagem(arquivo) {
        const tiposPermitidos = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif'
        ];
        const tamanhoMaximo = 5 * 1024 * 1024; // 5MB

        if (!arquivo) {
            return { valido: true };
        }

        if (!tiposPermitidos.includes(arquivo.type)) {
            return {
                valido: false,
                erro:
                    'Tipo de arquivo não permitido. Use apenas JPG, PNG ou GIF.'
            };
        }

        if (arquivo.size > tamanhoMaximo) {
            return {
                valido: false,
                erro: 'Arquivo muito grande. O tamanho máximo é 5MB.'
            };
        }

        return { valido: true };
    }
}
